import moment from 'moment';

/** `EDIT_SOMETHING` → "Edit something": the text of an action with no label yet. */
const humanizeAction = (action) => {
  const text = String(action || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

/**
 * What the user did, as a sentence fragment ("Avasi projektin"): the
 * translation of `activity_log.action.<ACTION>`, or the action code made
 * readable for an action that has none yet.
 * @param {Function} t i18next t
 * @param {string} action e.g. 'VIEW_PROJECT'
 * @returns {string}
 */
export const getActivityActionLabel = (t, action) =>
  t(`activity_log.action.${action}`, humanizeAction(action));

// Targets whose name adds to the sentence (an image's file name, a record's
// day); a project target's name is the project the feed belongs to.
const NAMED_TARGET_TYPES = ['FILEPERMISSION', 'IMAGE', 'RECORD'];

/**
 * The row's action with the target's name when it tells something:
 * "Katsoi kuvaa · IMG_0042.jpg".
 */
export const getActivityDescription = (t, item) => {
  const label = getActivityActionLabel(t, item.action);
  if (!item.targetName || !NAMED_TARGET_TYPES.includes(item.targetType)) {
    return label;
  }
  // A record is named by its day (YYYY-MM-DD).
  const name = /^\d{4}-\d{2}-\d{2}$/.test(item.targetName)
    ? moment(item.targetName).format('D.M.YYYY')
    : item.targetName;
  return `${label} · ${name}`;
};

/**
 * When it happened, as short as is unambiguous: the time today, the day and
 * the time this year, the date before that.
 * @param {string} createdAt ISO timestamp
 * @param {moment.Moment} [now]
 */
export const formatActivityTime = (createdAt, now = moment()) => {
  const at = moment(createdAt);
  if (at.isSame(now, 'day')) return at.format('HH:mm');
  if (at.isSame(now, 'year')) return at.format('D.M. HH:mm');
  return at.format('D.M.YYYY');
};

/** Two letters for an avatar without a picture: "Tatu Aalto" → "TA". */
export const getInitials = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

/**
 * The feed's rows grouped by the local day they happened on, newest first.
 * @param {Array<Object>} items
 * @returns {Array<{day: string, items: Array<Object>}>} day as YYYY-MM-DD
 */
export const groupActivityByDay = (items) => {
  const groups = [];
  (items || []).forEach((item) => {
    const day = moment(item.createdAt).format('YYYY-MM-DD');
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.items.push(item);
    } else {
      groups.push({ day, items: [item] });
    }
  });
  return groups;
};

/**
 * The feed's rows from `since` on (a moment, e.g. the local midnight).
 * @param {Array<Object>} items
 * @param {moment.Moment} since
 */
export const activitySince = (items, since) => {
  const from = since.valueOf();
  return (items || []).filter((item) => {
    const at = Date.parse(item.createdAt);
    return Number.isFinite(at) && at >= from;
  });
};

/**
 * The feed's rows grouped by who did them, the user with the newest row
 * first, each user's rows newest first. A row without a userId is grouped by
 * its userName.
 * @param {Array<Object>} items
 * @returns {Array<{key: string, userId: ?string, userName: ?string,
 *   items: Array<Object>}>}
 */
export const groupActivityByUser = (items) => {
  const newestFirst = [...(items || [])].sort((a, b) =>
    (b.createdAt || '').localeCompare(a.createdAt || '')
  );
  const groups = new Map();
  newestFirst.forEach((item) => {
    const key = item.userId
      ? `id:${item.userId}`
      : `name:${item.userName || ''}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        userId: item.userId || null,
        userName: item.userName || null,
        items: [],
      });
    }
    groups.get(key).items.push(item);
  });
  return [...groups.values()];
};
